import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import {
  Truck, CalendarClock, Wrench, AlertTriangle, Leaf, Bell, ArrowRight,
} from "lucide-react";

const PERIODS = [
  { value: "month", key: "vco2.period.month" },
  { value: "quarter", key: "vco2.period.quarter" },
  { value: "year", key: "vco2.period.year" },
];

function StatCard({ icon: Icon, label, value, tint, testid, suffix }) {
  return (
    <div
      className="border border-[var(--ink-200)] bg-[var(--bone)] p-5 flex flex-col gap-2"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 label-tech" style={{ color: tint }}>
        <Icon size={14} /> {label}
      </div>
      <div className="font-display text-3xl tracking-tighter">
        {value}
        {suffix && <span className="text-base ml-1 text-[var(--ink-500)]">{suffix}</span>}
      </div>
    </div>
  );
}

function MiniBar({ data, max, format }) {
  if (!data?.length) return null;
  const m = max || Math.max(...data.map((d) => d.co2_kg)) || 1;
  return (
    <div className="flex items-end gap-1 h-24" data-testid="vdash-trend-bars">
      {data.map((d) => (
        <div key={d.period} className="flex-1 flex flex-col items-center gap-1">
          <div
            className="w-full bg-[var(--moss)]"
            style={{ height: `${Math.max(2, (d.co2_kg / m) * 88)}px` }}
            title={`${d.period}: ${format(d.co2_kg)}`}
          />
          <div className="text-[9px] font-mono-tech text-[var(--ink-500)] truncate w-full text-center">
            {d.period.slice(-2)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function VehiclesOverview() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [periodType, setPeriodType] = useState("month");
  const [projects, setProjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [projectFilter, setProjectFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");

  useEffect(() => {
    if (!user) return;
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const params = { period_type: periodType };
    if (projectFilter) params.project_id = projectFilter;
    if (vehicleFilter) params.vehicle_id = vehicleFilter;
    api.get("/vehicles-dashboard", { params }).then((r) => setData(r.data)).catch(() => {});
  }, [user, periodType, projectFilter, vehicleFilter]);

  if (!user) {
    return (
      <Layout>
        <div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div>
      </Layout>
    );
  }

  const fmt = (n) => `${Math.round(n).toLocaleString()} kg`;

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-tech">{t("veh.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("veh.title")}</h1>
          </div>
          <Link to="/vehicles/list" className="btn-primary" data-testid="vehicles-new-link">
            <Truck size={14} /> {t("veh.new")}
          </Link>
        </div>

        <VehiclesSubNav />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 mb-6" data-testid="vdash-filters">
          <select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            className="input-tech max-w-[180px]"
            data-testid="vdash-period-select"
          >
            {PERIODS.map((p) => (
              <option key={p.value} value={p.value}>{t(p.key)}</option>
            ))}
          </select>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="input-tech max-w-[220px]"
            data-testid="vdash-project-filter"
          >
            <option value="">{t("vco2.allProjects")}</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.namn}</option>
            ))}
          </select>
          <select
            value={vehicleFilter}
            onChange={(e) => setVehicleFilter(e.target.value)}
            className="input-tech max-w-[200px]"
            data-testid="vdash-vehicle-filter"
          >
            <option value="">{t("vco2.allVehicles")}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>{v.regnr}</option>
            ))}
          </select>
        </div>

        {/* Top stat row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={CalendarClock}
            label={t("vdash.upcomingInspections")}
            value={data?.upcoming_inspections_count ?? 0}
            tint="var(--clay)"
            testid="vdash-card-inspections"
          />
          <StatCard
            icon={Wrench}
            label={t("vdash.upcomingServices")}
            value={data?.upcoming_services_count ?? 0}
            tint="var(--clay)"
            testid="vdash-card-services"
          />
          <StatCard
            icon={AlertTriangle}
            label={t("vdash.outOfCompliance")}
            value={data?.out_of_compliance_count ?? 0}
            tint="var(--brick)"
            testid="vdash-card-compliance"
          />
          <StatCard
            icon={Bell}
            label={t("vdash.activeReminders")}
            value={data?.upcoming_reminders?.length ?? 0}
            tint="var(--ink)"
            testid="vdash-card-reminders"
          />
        </div>

        {/* CO2 totals + trend */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <StatCard icon={Leaf} label={t("vco2.month")} value={fmt(data?.co2_month || 0)} tint="var(--moss)" testid="vdash-co2-month" suffix={t("vco2.kgCO2")} />
          <StatCard icon={Leaf} label={t("vco2.quarter")} value={fmt(data?.co2_quarter || 0)} tint="var(--moss)" testid="vdash-co2-quarter" suffix={t("vco2.kgCO2")} />
          <StatCard icon={Leaf} label={t("vco2.year")} value={fmt(data?.co2_year || 0)} tint="var(--moss)" testid="vdash-co2-year" suffix={t("vco2.kgCO2")} />
        </div>

        {/* Trend + top vehicles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vdash-trend-card">
            <div className="label-tech mb-3">{t("vco2.trend")}</div>
            {data?.series?.length ? (
              <MiniBar data={data.series} format={fmt} />
            ) : (
              <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>
            )}
          </div>
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vdash-top-vehicles">
            <div className="label-tech mb-3">{t("vco2.topVehicles")}</div>
            {data?.top_vehicles?.length ? (
              <div className="space-y-2">
                {data.top_vehicles.slice(0, 6).map((v) => (
                  <Link key={v.vehicle_id} to={`/vehicles/${v.vehicle_id}`} className="flex items-center justify-between text-sm hover:bg-[var(--surface-alt)] px-2 py-1.5 -mx-2">
                    <span className="font-mono-tech">{v.regnr || v.vehicle_id.slice(0, 8)}</span>
                    <span className="text-[var(--ink-700)]">{fmt(v.co2_kg)} CO₂e · {v.distance_km} km</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>
            )}
          </div>
        </div>

        {/* Per project + upcoming lists */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vdash-per-project">
            <div className="label-tech mb-3">{t("vco2.perProject")}</div>
            {data?.per_project?.length ? (
              <div className="space-y-2">
                {data.per_project.slice(0, 8).map((p, i) => (
                  <div key={p.project_id || `none-${i}`} className="flex items-center justify-between text-sm">
                    <span>{p.project_name || <span className="italic text-[var(--ink-500)]">— {t("vco2.allProjects").toLowerCase()} —</span>}</span>
                    <span className="font-mono-tech text-[var(--moss)]">{fmt(p.co2_kg)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>
            )}
          </div>
          <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="vdash-upcoming">
            <div className="flex items-center justify-between mb-3">
              <div className="label-tech">{t("vdash.upcomingInspections")}</div>
              <Link to="/vehicles/inspections" className="label-tech text-[var(--moss)]">{t("vdash.viewAll")}</Link>
            </div>
            {data?.upcoming_inspections?.length ? (
              <div className="space-y-2">
                {data.upcoming_inspections.slice(0, 6).map((i) => (
                  <div key={i.id} className="flex items-center justify-between text-sm">
                    <span>{(i.date || "").slice(0, 10)}</span>
                    <Link to={`/vehicles/${i.vehicle_id}`} className="font-mono-tech inline-flex items-center gap-1">
                      {i.vehicle_id?.slice(0, 8)} <ArrowRight size={12} />
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-[var(--ink-500)]">{t("vdash.noData")}</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
